import { useCallback, useEffect, useRef, useState } from 'react';

import { clamp } from 'lodash';

import { type CounterProps, type CounterEvent } from './Counter';
import { ceilQuantity, round } from './Counter.utils';

const useCounter = (props: Readonly<CounterProps>) => {
  const {
    quantity: initialQuantity = 1,
    maxQuantity = Number.MAX_SAFE_INTEGER,
    multiplier = 1,
    onChange,
  } = props;

  const [quantity, setQuantity] = useState(
    ceilQuantity(initialQuantity, multiplier)
  );

  const [value, setValue] = useState<string>(String(quantity));

  const previousQuantity = useRef(quantity);
  const inputRef = useRef<HTMLInputElement>(null);

  const canDecrease = quantity > multiplier;
  const canIncrease = maxQuantity - quantity > 0;

  const updateQuantity = useCallback(
    (props: { quantity: number; event: CounterEvent }) => {
      const { event, quantity } = props;

      const newQuantity = clamp(
        round(ceilQuantity(quantity, multiplier)),
        multiplier,
        maxQuantity
      );

      setValue(String(newQuantity));
      setQuantity(newQuantity);

      if (previousQuantity.current !== newQuantity) {
        onChange({
          event,
          action:
            newQuantity > previousQuantity.current ? 'increment' : 'decrement',
          quantity: newQuantity,
          unitQuantity: round(newQuantity / multiplier),
        });

        previousQuantity.current = newQuantity;
      }
    },
    [maxQuantity, multiplier, onChange]
  );

  const handleDecrement = () =>
    updateQuantity({
      event: 'click',
      quantity: quantity - multiplier,
    });

  const handleIncrement = () =>
    updateQuantity({
      event: 'click',
      quantity: quantity + multiplier,
    });

  const handleInputChange = (event: { target: HTMLInputElement }) => {
    const eventValue = event.target.value;

    setValue(eventValue);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter') {
      inputRef.current?.blur();
    }
  };

  const handleInputBlur = (event: { target: HTMLInputElement }) => {
    const newQuantity = parseFloat(event.target.value || '0');

    updateQuantity({
      event: 'inputChange',
      quantity: newQuantity,
    });
  };

  useEffect(() => {
    updateQuantity({
      event: 'inputChange',
      quantity: initialQuantity,
    });
  }, [initialQuantity, updateQuantity]);

  return {
    value,
    inputRef,
    canIncrease,
    canDecrease,
    handleDecrement,
    handleIncrement,
    handleInputChange,
    handleInputKeyDown,
    handleInputBlur,
  };
};

export default useCounter;
